import { useState } from "react"
import { Card, CardContent } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { ChevronLeft, ChevronRight, Quote, Star } from "lucide-react"
import { useLanguage } from "@/lib/i18n"

const testimonials = [
  {
    type: "driver",
    name: "Miguel Rodriguez",
    role: "OTR Driver, 3 Years",
    image: "/professional-truck-driver-hispanic-man.jpg",
    quote: {
      en: "After 15 years in the industry, Reyes Trux Corp is hands down the best company I've worked for. The pay is consistent, the equipment is top-notch, and they actually care about getting me home to my family.",
      es: "Despues de 15 anos en la industria, Reyes Trux Corp es sin duda la mejor empresa para la que he trabajado. El pago es consistente, el equipo es de primera y realmente se preocupan por que llegue a casa con mi familia.",
    },
    rating: 5,
  },
  {
    type: "client",
    name: "Sarah Chen",
    role: "Logistics Manager, TechCorp Inc.",
    image: "/professional-business-woman-asian.jpg",
    quote: {
      en: "We've been partnering with Reyes Trux Corp for over 3 years. Their reliability is unmatched-98% on-time delivery and excellent communication. They're not just a vendor, they're a true partner.",
      es: "Hemos sido socios de Reyes Trux Corp por mas de 3 anos. Su confiabilidad es inigualable-98% de entregas a tiempo y excelente comunicacion. No son solo un proveedor, son un verdadero socio.",
    },
    rating: 5,
  },
  {
    type: "driver",
    name: "James Williams",
    role: "Team Driver, 5 Years",
    image: "/professional-truck-driver-african-american-man.jpg",
    quote: {
      en: "The sign-on bonus was great, but what made me stay is the respect they show drivers. 24/7 support that actually helps, and dispatch that works with you, not against you.",
      es: "El bono de contratacion fue genial, pero lo que me hizo quedar es el respeto que muestran a los conductores. Soporte 24/7 que realmente ayuda, y despacho que trabaja contigo, no en tu contra.",
    },
    rating: 5,
  },
  {
    type: "client",
    name: "Robert Martinez",
    role: "Supply Chain Director, FreshFoods",
    image: "/professional-business-man-hispanic.jpg",
    quote: {
      en: "For our shipments on the East Coast, there's no one we trust more. Their fleet and trained drivers handle our products with the utmost care and professionalism.",
      es: "Para nuestros envios en la Costa Este, no hay nadie en quien confiemos mas. Su flota y conductores capacitados manejan nuestros productos con el maximo cuidado y profesionalismo.",
    },
    rating: 5,
  },
]

export default function TestimonialsSection() {
  const { language, t } = useLanguage()
  const [currentIndex, setCurrentIndex] = useState(0)

  const nextTestimonial = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length)
  }

  const prevTestimonial = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length)
  }

  return (
    <section className="py-20 bg-secondary">
      <div className="container mx-auto px-4">
        {/* Section header */}
        <div className="text-center max-w-3xl mx-auto mb-12">
          <p className="text-primary font-semibold tracking-wider uppercase mb-4">{t("testimonials.tagline")}</p>
          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-balance">{t("testimonials.title")}</h2>
        </div>

        {/* Main testimonial display */}
        <div className="max-w-4xl mx-auto">
          <Card className="bg-card border-border overflow-hidden">
            <CardContent className="p-8 md:p-12">
              <div className="flex flex-col md:flex-row gap-8 items-start">
                <div className="flex-shrink-0">
                  <div className="relative">
                    <img
                      src={testimonials[currentIndex].image || "/placeholder.svg"}
                      alt={testimonials[currentIndex].name}
                      className="w-20 h-20 rounded-full object-cover border-2 border-primary"
                    />
                    <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                      <Quote className="h-4 w-4 text-primary-foreground" />
                    </div>
                  </div>
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-1 mb-4">
                    {Array.from({ length: testimonials[currentIndex].rating }).map((_, i) => (
                      <Star key={i} className="h-5 w-5 fill-primary text-primary" />
                    ))}
                  </div>
                  <blockquote className="text-lg md:text-xl text-foreground leading-relaxed mb-6">
                    &ldquo;{testimonials[currentIndex].quote[language]}&rdquo;
                  </blockquote>
                  <div>
                    <p className="font-semibold text-foreground">{testimonials[currentIndex].name}</p>
                    <p className="text-sm text-muted-foreground">{testimonials[currentIndex].role}</p>
                    <span
                      className={`inline-block mt-2 px-3 py-1 rounded-full text-xs font-medium ${
                        testimonials[currentIndex].type === "driver"
                          ? "bg-primary/10 text-primary"
                          : "bg-accent/10 text-accent"
                      }`}
                    >
                      {testimonials[currentIndex].type === "driver"
                        ? t("testimonials.driver")
                        : t("testimonials.client")}
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Navigation */}
          <div className="flex items-center justify-between mt-8">
            <div className="flex gap-2">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`w-2 h-2 rounded-full transition-colors ${
                    index === currentIndex ? "bg-primary" : "bg-muted"
                  }`}
                  aria-label={`Go to testimonial ${index + 1}`}
                />
              ))}
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="icon"
                onClick={prevTestimonial}
                aria-label="Previous testimonial"
                className="border-primary/50 hover:bg-primary/10 bg-transparent"
              >
                <ChevronLeft className="h-5 w-5" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                onClick={nextTestimonial}
                aria-label="Next testimonial"
                className="border-primary/50 hover:bg-primary/10 bg-transparent"
              >
                <ChevronRight className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>

        {/* Testimonial grid preview */}
        <div className="grid md:grid-cols-4 gap-4 mt-12">
          {testimonials.map((testimonial, index) => (
            <button
              key={testimonial.name}
              onClick={() => setCurrentIndex(index)}
              className={`p-4 rounded-lg text-left transition-all ${
                index === currentIndex
                  ? "bg-primary/10 border border-primary"
                  : "bg-card border border-border hover:border-primary/50"
              }`}
            >
              <div className="flex items-center gap-3 mb-2">
                <img
                  src={testimonial.image || "/placeholder.svg"}
                  alt={testimonial.name}
                  className="w-10 h-10 rounded-full object-cover"
                />
                <div>
                  <p className="text-sm font-medium text-foreground truncate">{testimonial.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {testimonial.type === "driver" ? t("testimonials.driver") : t("testimonials.client")}
                  </p>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </section>
  )
}
